import _ from "lodash";
import {
  CSSProperties,
  Fragment,
  memo,
  ReactNode,
  Suspense,
  useRef,
  useState,
} from "react";
import AppSidebar from "./AppSidebar";
import AppBreadcrumb, { type AppBreadcrumbProps } from "./AppBreadcrumb";
import clsx from "clsx";
import { IQueueTypeOrArrayOrFunc } from "rc-queue-anim";
import {
  StyledAppContainer,
  StyledAppWrap,
  StyledAppWrapHeader,
  StyledMainContentCard,
  StyledMenuBtn,
} from "./index.styled";
import AppLoader from "../AppLoader";
// import { BreadcrumbProps } from "antd";
import AppPageMeta from "../AppPageMeta";
import {
  SidebarContextType,
  SidebarProvider,
} from "@lib/context/SidebarContext";
import { IoMenu } from "react-icons/io5";
import { AppLayout, AppSticky, AppTabs } from "@lib/components/AppComponents";
import { Col, Row } from "antd";
import { Dayjs } from "dayjs";
import { DateRangeFilter } from "@lib/components/MiniComponents/Others";
import { useTheme } from "styled-components";
import { AppCardProps } from "@lib/components/AppComponents/AppCard";
import { AppTabsProps } from "../AppTabs";
import { AdminSidebarMenuProps } from "../AppLayout/Admin/AdminSidebar";

import AppsContainerSidebarMenu from "./AppsContainerSidebarMenu";
import AppsContainerSidebarCard from "./AppsContainerSidebarCard";
import AppsContainerSidebarUserCard from "./AppsContainerSidebarUserCard";
import AppsContainerSidebar from "./AppsContainerSidebar";

interface AppsContainerProps {
  title: string | ReactNode;
  type?: IQueueTypeOrArrayOrFunc;
  noContentAnimation?: boolean;
  fullView?: boolean;
  sidebarContent?: ReactNode;
  children?: ReactNode;
  cardBg?: boolean;
  cardStyle?: CSSProperties;
  cardProps?: AppCardProps;
  sidebarProviderProps?: SidebarContextType;
  isLoading?: boolean;
  showBreadcrumb?: boolean;
  breadcrumbProps?: AppBreadcrumbProps;
  noPadding?: boolean;
  noContainer?: boolean;
  noLayout?: boolean;
  minusHeight?: number;
  height?: number;
  // Date range props
  showDateRange?: boolean;
  dateRangeValue?: [string, string];
  onDateRangeChange?: (value: [Dayjs, Dayjs]) => void;
  tabs?: AppTabsProps;
  menuProps?: AdminSidebarMenuProps;
}

const MainContent = memo(
  (props: AppsContainerProps) => {
    const {
      noContentAnimation = false,
      noContainer,
      sidebarContent,
      children = null,
      cardStyle,
      cardBg = false,
      cardProps,
      isLoading,
      height,
      tabs,
    } = props;

    return (
      <StyledMainContentCard
        key="content"
        // variant="borderless"
        style={{
          overflow: sidebarContent ? "hidden" : "visible",
          padding: 0,
          ...(!noContainer && { marginTop: 10 }),
          // minHeight: `calc(100vh - ${minusHeight}px)`,
          ...(height ? { maxHeight: `${height}px` } : {}),
          ...(tabs
            ? { boxShadow: "none", borderRadius: "unset!important" }
            : {}),
          ...cardStyle,
        }}
        bg={cardBg}
        {...cardProps}
      >
        {noContentAnimation && isLoading ? (
          <AppLoader />
        ) : (
          <>
            {tabs && <AppTabs {...tabs} />}
            {children}
          </>
        )}
      </StyledMainContentCard>
    );
  },
  (prevProps, nextProps) => {
    return _.isEqual(prevProps, nextProps);
  },
);

const AppContainerComponent = memo(
  (props: AppsContainerProps) => {
    const {
      title = "",
      // noContentAnimation = false,
      sidebarContent,
      fullView,
      // children = null,
      // cardStyle,
      // cardBg = false,
      // cardProps,
      sidebarProviderProps,
      // isLoading,
      showBreadcrumb = true,
      breadcrumbProps,
      noPadding = false,
      noContainer = false,
      noLayout = false,
      // minusHeight = 0,
      height,
      showDateRange = false,
      dateRangeValue,
      onDateRangeChange,
      // tabs,
      menuProps,
    } = props;

    // const { messages } = useIntl();
    const scrollItem = useRef<HTMLElement>(null);
    const theme = useTheme();
    const [isAppDrawerOpen, setAppDrawerOpen] = useState(false);
    // const { footer, navStyle } = useLayoutContext();

    if (noContainer) {
      return (
        <Suspense fallback={<AppLoader />}>
          <StyledAppContainer height={height}>
            <MainContent {...props} />
          </StyledAppContainer>
        </Suspense>
      );
    }

    const content = (
      <StyledAppWrap noPadding={noPadding} ref={scrollItem as any}>
        {title && <AppPageMeta title={title as string} />}

        {(showBreadcrumb || showDateRange) && (
          <AppSticky
            top={0}
            style={{ margin: "0 0 4px" }}
            stickyStyle={{
              border: `2px dotted ${theme.palette.primary.main}`,
              borderRadius: "8px",
              padding: "4px 6px",
            }}
            stickyBgColor={theme.palette.background.secondary}
            scrollItem={
              document.querySelector(".ant-layout-content") as HTMLElement
            }
          >
            <Row justify="space-between" align="middle" style={{ gap: 5 }}>
              {showBreadcrumb && (
                <Col
                  xs={24}
                  sm={24}
                  md={showDateRange ? 16 : 24}
                  lg={showDateRange ? 16 : 24}
                  xl={showDateRange ? 16 : 24}
                  xxl={showDateRange ? 16 : 24}
                >
                  <AppBreadcrumb {...breadcrumbProps} />
                </Col>
              )}

              {showDateRange && (
                <Col xs={24} sm={24} md={8} lg={6} xl={6} xxl={6}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                    }}
                  >
                    <DateRangeFilter
                      value={dateRangeValue || ["", ""]}
                      onChange={onDateRangeChange}
                    />
                  </div>
                </Col>
              )}
            </Row>
          </AppSticky>
        )}

        {sidebarContent && (
          <StyledAppWrapHeader
            className={clsx({
              appsWrapHeaderFull: fullView && !sidebarContent,
            })}
          >
            <StyledMenuBtn onClick={() => setAppDrawerOpen(!isAppDrawerOpen)}>
              {/* @ts-ignore */}
              <IoMenu size={25} className="menu-btn-icon" />
            </StyledMenuBtn>
          </StyledAppWrapHeader>
        )}

        <StyledAppContainer>
          {sidebarContent && (
            <SidebarProvider {...sidebarProviderProps}>
              <AppSidebar
                isAppDrawerOpen={isAppDrawerOpen}
                setAppDrawerOpen={setAppDrawerOpen}
                // footer={footer}
                fullView={fullView && !sidebarContent}
                // navStyle={navStyle}
                sidebarContent={sidebarContent}
                key="sidebar"
              />
            </SidebarProvider>
          )}

          <MainContent {...props} />
        </StyledAppContainer>
      </StyledAppWrap>
    );

    return (
      <Suspense fallback={<AppLoader />}>
        {noLayout ? (
          <Fragment>{content}</Fragment>
        ) : (
          <AppLayout menuProps={menuProps}>{content}</AppLayout>
        )}
      </Suspense>
    );
  },
  (prevProps, nextProps) => {
    return _.isEqual(prevProps, nextProps);
  },
);

const AppsContainer = Object.assign(AppContainerComponent, {
  Sidebar: AppsContainerSidebar,
  SidebarMenu: AppsContainerSidebarMenu,
  SidebarCard: AppsContainerSidebarCard,
  SidebarUserCard: AppsContainerSidebarUserCard,
});

export default AppsContainer;
